import React from "react";
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DiningOutlinedIcon from "@mui/icons-material/DiningOutlined";
import MedicalInformationOutlinedIcon from "@mui/icons-material/MedicalInformationOutlined";
import { DIETARY_OPTIONS, FOOD_ALLERGY_OPTIONS } from "../../../constants/childProfileOptions";
import colors from "../../../assets/theme/colors";
import { CustomizableAutocomplete } from "../../UI";
import {
  ALLERGY_SUGGESTION_OPTIONS,
  DIETARY_SUGGESTION_OPTIONS,
} from "./profileDropdownOptions";

const NO_KNOWN_ALLERGIES_LABEL = "None";

const sectionShellSx = {
  p: { xs: 1.35, sm: 1.6 },
  borderRadius: "22px",
  border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
  bgcolor: colors.semantic.profileFlow.cardBg,
};

const fieldSx = {
  "& .MuiAutocomplete-tag": {
    display: "none",
  },
  "& .MuiInputBase-root": {
    minHeight: 54,
    borderRadius: "16px",
    bgcolor: "#fff",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.82rem",
    fontWeight: 800,
    color: colors.semantic.profileFlow.fieldLabel,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: colors.semantic.profileFlow.cardBorder,
  },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: colors.semantic.profileFlow.focusBorder,
    borderWidth: "1px",
  },
};

const normalizeList = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const itemLabel = (item) => {
  if (typeof item === "string") return item;
  return item?.label || item?.name || String(item || "");
};

const hasItem = (items, item) => {
  const nextLabel = itemLabel(item).trim().toLowerCase();
  return normalizeList(items).some((existing) => itemLabel(existing).trim().toLowerCase() === nextLabel);
};

const addItem = (items, item) => (hasItem(items, item) ? normalizeList(items) : [...normalizeList(items), item]);

const removeItem = (items, item) => {
  const removeLabel = itemLabel(item).trim().toLowerCase();
  return normalizeList(items).filter((existing) => itemLabel(existing).trim().toLowerCase() !== removeLabel);
};

const isNoKnownAllergies = (item) => itemLabel(item).trim().toLowerCase() === NO_KNOWN_ALLERGIES_LABEL.toLowerCase();

const normalizeAllergySelections = (items) => {
  const normalizedItems = normalizeList(items);
  if (!normalizedItems.some(isNoKnownAllergies)) {
    return normalizedItems;
  }

  const lastItem = normalizedItems[normalizedItems.length - 1];
  return isNoKnownAllergies(lastItem)
    ? [NO_KNOWN_ALLERGIES_LABEL]
    : normalizedItems.filter((item) => !isNoKnownAllergies(item));
};

const addAllergyItem = (items, item) => normalizeAllergySelections(addItem(items, item));

const SectionIntro = ({ childName }) => (
  <Box
    sx={{
      p: 1.35,
      borderRadius: "14px",
      border: `1px solid ${alpha(colors.brand.ink, 0.55)}`,
      bgcolor: alpha(colors.brand.ink, 0.12),
    }}
  >
    <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: colors.landing.heroText }}>
      What everyone should know before feeding {childName}
    </Typography>
    <Typography sx={{ mt: 0.25, fontSize: "0.84rem", color: colors.landing.bodyText, lineHeight: 1.45 }}>
      Add allergies and dietary needs so any caregiver sees them quickly.
    </Typography>
  </Box>
);

const SectionHeader = ({ icon, title, count, subtitle, tone = "allergy" }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "12px",
        display: "grid",
        placeItems: "center",
        bgcolor: tone === "allergy" ? "#fde6e7" : "#f8dfd1",
        color: tone === "allergy" ? "#9b272c" : "#784124",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={0.7} alignItems="center">
        <Typography sx={{ fontSize: "1.02rem", fontWeight: 850, color: colors.landing.heroText }}>
          {title}
        </Typography>
        <Chip
          label={count}
          size="small"
          sx={{
            height: 22,
            minWidth: 28,
            bgcolor: colors.semantic.profileFlow.nudgeBg,
            color: colors.landing.bodyText,
            fontWeight: 800,
          }}
        />
      </Stack>
      <Typography sx={{ fontSize: "0.8rem", color: colors.landing.bodyText }}>
        {subtitle}
      </Typography>
    </Box>
  </Stack>
);

const SuggestionPills = ({ options, selected, onAdd, tone = "allergy" }) => (
  <Stack direction="row" flexWrap="wrap" gap={0.75}>
    {options.filter((option) => !hasItem(selected, option)).map((option) => (
      <Button
        key={option}
        size="small"
        startIcon={<AddRoundedIcon />}
        onClick={() => onAdd(option)}
        sx={{
          borderRadius: "999px",
          px: 1.05,
          py: 0.35,
          minHeight: 0,
          border: `1px dashed ${tone === "allergy" ? "#b23a3f" : "#8b5130"}`,
          color: tone === "allergy" ? "#9b272c" : "#75401f",
          bgcolor: "#fff",
          fontWeight: 800,
          textTransform: "none",
          "& .MuiButton-startIcon": { mr: 0.2 },
        }}
      >
        {option}
      </Button>
    ))}
  </Stack>
);

const AllergyCard = ({ allergy, onRemove }) => {
  const label = itemLabel(allergy);

  return (
    <Box
      sx={{
        p: 1,
        borderRadius: "14px",
        border: `1px solid ${alpha("#b23a3f", 0.28)}`,
        bgcolor: "#fff",
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 0.75,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={0.65} alignItems="center" flexWrap="wrap">
          <Typography sx={{ fontSize: "0.92rem", fontWeight: 850, color: colors.landing.heroText }}>
            {label}
          </Typography>
        </Stack>
      </Box>
      <Button
        aria-label={`Remove ${label}`}
        onClick={() => onRemove(allergy)}
        sx={{ minWidth: 0, width: 32, height: 32, borderRadius: "50%", color: colors.landing.bodyText }}
      >
        <CloseRoundedIcon fontSize="small" />
      </Button>
    </Box>
  );
};

const DietaryCard = ({ item, onRemove }) => {
  const label = itemLabel(item);
  return (
    <Box
      sx={{
        p: 1,
        borderRadius: "14px",
        bgcolor: "#f9ded1",
        color: "#75401f",
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 0.75,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.88rem", fontWeight: 850 }}>{label}</Typography>
        <Typography sx={{ fontSize: "0.76rem", color: "#8b5a40" }}>
          Add details if caregivers need context.
        </Typography>
      </Box>
      <Button
        aria-label={`Remove ${label}`}
        onClick={() => onRemove(item)}
        sx={{ minWidth: 0, width: 30, height: 30, borderRadius: "50%", color: "#75401f" }}
      >
        <CloseRoundedIcon fontSize="small" />
      </Button>
    </Box>
  );
};

const AllergiesFoodSection = ({
  childName = "this person",
  foodAllergies,
  setFoodAllergies,
  dietaryRestrictions,
  setDietaryRestrictions,
  renderDocumentDropZone,
}) => {
  const allergies = normalizeList(foodAllergies);
  const dietaryNeeds = normalizeList(dietaryRestrictions);

  return (
    <Box sx={sectionShellSx}>
      <Stack spacing={1.6}>
        <SectionIntro childName={childName} />

        <Stack spacing={1}>
          <SectionHeader
            icon={<MedicalInformationOutlinedIcon fontSize="small" />}
            title="Allergies"
            count={allergies.length}
            subtitle="Pick known allergies caregivers should see"
          />
          <CustomizableAutocomplete
            options={[NO_KNOWN_ALLERGIES_LABEL, ...FOOD_ALLERGY_OPTIONS]}
            value={allergies}
            onChange={(event, nextValue) => setFoodAllergies(normalizeAllergySelections(nextValue))}
            label="Type an allergy, or pick from the list"
            addText="Add allergy"
            helperText=" "
            sx={fieldSx}
          />
          {allergies.length > 0 && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 0.8 }}>
              {allergies.map((allergy) => (
                <AllergyCard key={itemLabel(allergy)} allergy={allergy} onRemove={(item) => setFoodAllergies(removeItem(allergies, item))} />
              ))}
            </Box>
          )}
          <SuggestionPills options={ALLERGY_SUGGESTION_OPTIONS} selected={allergies} onAdd={(item) => setFoodAllergies(addAllergyItem(allergies, item))} />
        </Stack>

        <Box sx={{ borderTop: `1px solid ${colors.semantic.profileFlow.cardBorder}` }} />

        <Stack spacing={1}>
          <SectionHeader
            icon={<DiningOutlinedIcon fontSize="small" />}
            title="Dietary needs"
            count={dietaryNeeds.length}
            subtitle="Preferences, restrictions, and how they eat"
            tone="diet"
          />
          <CustomizableAutocomplete
            options={DIETARY_OPTIONS}
            value={dietaryNeeds}
            onChange={(event, nextValue) => setDietaryRestrictions(nextValue)}
            label="Type a restriction, or pick from the list"
            addText="Add diet"
            helperText=" "
            sx={fieldSx}
          />
          {dietaryNeeds.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.85}>
              {dietaryNeeds.map((item) => (
                <DietaryCard key={itemLabel(item)} item={item} onRemove={(removed) => setDietaryRestrictions(removeItem(dietaryNeeds, removed))} />
              ))}
            </Stack>
          )}
          <SuggestionPills options={DIETARY_SUGGESTION_OPTIONS} selected={dietaryNeeds} onAdd={(item) => setDietaryRestrictions(addItem(dietaryNeeds, item))} tone="diet" />
        </Stack>

        <Box sx={{ borderTop: `1px solid ${colors.semantic.profileFlow.cardBorder}` }} />
        {renderDocumentDropZone?.("medical", "Care documents", "Action plans, doctor's notes, dietary guidance, or other helpful documents.")}
      </Stack>
    </Box>
  );
};

export default AllergiesFoodSection;
